'use client';

import React, { useState } from 'react';
import { X, Copy, Check, Terminal, Cpu, Sparkles, ExternalLink, ShieldCheck, Zap } from 'lucide-react';

interface BuyerMcpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BuyerMcpModal({ isOpen, onClose }: BuyerMcpModalProps) {
  const [tab, setTab] = useState<'claude' | 'cursor' | 'antigravity' | 'stdio'>('claude');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  if (!isOpen) return null;

  const mcpServerUrl = 'https://mcp.parts4drive.com/sse';
  const mcpHttpUrl = 'https://mcp.parts4drive.com/mcp';

  const copyToClipboard = (text: string, key: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const claudeConfig = JSON.stringify(
    {
      mcpServers: {
        parts4drive: {
          url: mcpServerUrl
        }
      }
    },
    null,
    2
  );

  const cursorConfig = JSON.stringify(
    {
      mcpServers: {
        parts4drive: {
          url: mcpHttpUrl
        }
      }
    },
    null,
    2
  );

  const antigravityConfig = JSON.stringify(
    {
      mcpServers: {
        parts4drive: {
          command: 'npx',
          args: ['-y', '@parts4drive/mcp']
        }
      }
    },
    null,
    2
  );

  const stdioCommand = 'npx @parts4drive/mcp';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-slate-900 border border-slate-800 text-slate-100 shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-slate-800">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
              <Zap className="size-3.5" />
              <span>Public Model Context Protocol (RFC 2024-11-05)</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2 pt-1">
              Connect Your AI Agent to Parts4Drive
            </h3>
            <p className="text-xs sm:text-sm text-slate-400">
              Query millions of authentic auto parts, verify VIN fitment, and check real-time African depot inventory directly inside Claude Desktop, Cursor, and custom autonomous agents.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Public Buyer Tools Manifest */}
        <div className="my-6 p-4 rounded-2xl bg-slate-950/60 border border-slate-800/80 space-y-3">
          <div className="flex items-center justify-between text-xs font-bold text-slate-300 uppercase tracking-wider">
            <span className="flex items-center gap-1.5">
              <Sparkles className="size-3.5 text-amber-400" />
              Public Tools Available (Zero Auth Required)
            </span>
            <span className="text-[11px] text-emerald-400 bg-emerald-950/50 px-2 py-0.5 rounded-full border border-emerald-800">
              Free Access
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
              <div className="font-mono font-bold text-white flex items-center justify-between">
                <span>buyer_search_parts</span>
                <span className="text-[10px] text-slate-400">GH, NG, KE, ZA, CI</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                Search verified catalogs by keyword, vehicle make/model, and category with genuine local currency pricing.
              </p>
            </div>

            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
              <div className="font-mono font-bold text-white">p4d_verify_vin_fitment</div>
              <p className="text-[11px] text-slate-400 mt-1">
                Decode 17-digit VINs and verify MMYTE fitment compatibility against genuine inventory.
              </p>
            </div>

            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
              <div className="font-mono font-bold text-white">p4d_get_part_details</div>
              <p className="text-[11px] text-slate-400 mt-1">
                Full technical specifications, OEM reference cross-match, warranty, and depot physical address.
              </p>
            </div>

            <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800">
              <div className="font-mono font-bold text-white">p4d_request_part_quote</div>
              <p className="text-[11px] text-slate-400 mt-1">
                Submit automated sourcing requests to verified hub merchants for rare and vintage components.
              </p>
            </div>
          </div>
        </div>

        {/* Endpoints */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="font-semibold uppercase tracking-wider text-[11px]">Streamable HTTP Endpoint</span>
              <button
                type="button"
                onClick={() => copyToClipboard(mcpHttpUrl, 'http')}
                className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1 text-[11px]"
              >
                {copiedKey === 'http' ? <Check className="size-3" /> : <Copy className="size-3" />}
                {copiedKey === 'http' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="font-mono text-xs text-white truncate bg-slate-900 p-2 rounded-lg border border-slate-800">
              {mcpHttpUrl}
            </div>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span className="font-semibold uppercase tracking-wider text-[11px]">Remote SSE Endpoint</span>
              <button
                type="button"
                onClick={() => copyToClipboard(mcpServerUrl, 'sse')}
                className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1 text-[11px]"
              >
                {copiedKey === 'sse' ? <Check className="size-3" /> : <Copy className="size-3" />}
                {copiedKey === 'sse' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="font-mono text-xs text-white truncate bg-slate-900 p-2 rounded-lg border border-slate-800">
              {mcpServerUrl}
            </div>
          </div>
        </div>

        {/* Config Tabs */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-2 overflow-x-auto no-scrollbar">
            {[
              { id: 'claude', label: 'Claude Desktop' },
              { id: 'cursor', label: 'Cursor' },
              { id: 'antigravity', label: 'Antigravity / Windsurf' },
              { id: 'stdio', label: 'Stdio CLI' }
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id as any)}
                className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all ${
                  tab === t.id
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative rounded-2xl bg-slate-950 border border-slate-800 p-4 font-mono text-xs overflow-x-auto shadow-inner">
            <div className="flex items-center justify-between text-[11px] text-slate-400 mb-2 border-b border-slate-800 pb-2">
              <span>
                {tab === 'claude' && 'claude_desktop_config.json'}
                {tab === 'cursor' && '.cursor/mcp.json'}
                {tab === 'antigravity' && 'antigravity_mcp_config.json'}
                {tab === 'stdio' && 'Terminal Execution'}
              </span>
              <button
                type="button"
                onClick={() => {
                  const toCopy =
                    tab === 'claude'
                      ? claudeConfig
                      : tab === 'cursor'
                      ? cursorConfig
                      : tab === 'antigravity'
                      ? antigravityConfig
                      : stdioCommand;
                  copyToClipboard(toCopy, 'config');
                }}
                className="flex items-center gap-1 text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 px-2.5 py-1 rounded-lg transition-colors"
              >
                {copiedKey === 'config' ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
                <span>{copiedKey === 'config' ? 'Copied' : 'Copy Configuration'}</span>
              </button>
            </div>

            <pre className="text-emerald-400 leading-relaxed overflow-x-auto">
              {tab === 'claude' && claudeConfig}
              {tab === 'cursor' && cursorConfig}
              {tab === 'antigravity' && antigravityConfig}
              {tab === 'stdio' && stdioCommand}
            </pre>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
          <span className="flex items-center gap-1.5">
            <ShieldCheck className="size-4 text-emerald-400" />
            No API key or authentication required for buyer discovery tools.
          </span>
          <a
            href="https://parts4drive.com/llms.txt"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white flex items-center gap-1 text-[11px] text-blue-400"
          >
            <span>llms.txt</span>
            <ExternalLink className="size-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
